// ADR 0023 Sprint 7 Wave B — Versioned Settings Screen.
//
// Generic shared component for all 6 per-tenant settings domains
// (banking | fraud | aml | case-workflow | notification | reporting-retention).
//
// Each domain screen is a ~15-line wrapper that supplies:
//   - domain slug and labels
//   - a zod schema for its payload shape
//   - defaultValues for an empty draft form
//   - renderFields: how to render the form fields
//   - optional renderVersionSummary: how to summarise a version row
//
// Maker-checker flow (mirrors ApprovalsQueuePage pattern):
//   1. User clicks "Activate" on a draft version.
//   2. Component calls submitApproval with the domain's entity_type +
//      the version's public_id as entity_id.
//   3. User is shown an info state: "Approval submitted — a checker must
//      approve and apply it before activation is possible."
//   4. Once the approval is in 'applied' state (the user polls or the
//      checker applies it in ApprovalsQueuePage), a second "Activate now"
//      button calls activateSettings with {settings_row_id, approval_public_id}.
//   5. On 422: surfaces "Approval required or not yet applied."
//   6. On 409 approval_entity_id_mismatch: surfaces clear message.
//
// Security:
//   - Never logs form values (payload may contain PII for notification domain).
//   - Disable submit while pending to prevent double-submit.
//   - All mutations invalidate the query cache.
import { useState } from 'react'
import type { ReactNode } from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  useForm,
} from 'react-hook-form'
import type { UseFormReturn, FieldValues, DefaultValues } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { z } from 'zod'
import { toast } from 'sonner'
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FilePlus,
  FileText,
  Info,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Skeleton } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { ApiError } from '@/api/client'
import {
  submitApproval,
  getApproval,
  isTenantInternalApprovalEntityType,
} from '@/features/databus/api-approvals'
import type { ApprovalRecord } from '@/features/databus/api-approvals'
import {
  getActiveSettings,
  listSettingsVersions,
  createSettingsDraft,
  activateSettings,
  DOMAIN_APPROVAL_ENTITY_TYPE,
} from '../api-settings'
import type { SettingsDomain, SettingsVersion } from '../types'

// ---- Props -------------------------------------------------------------------
//
// VersionedSettingsScreen is generic over TPayload (extends FieldValues) so that
// each domain screen can provide a fully-typed renderFields callback. The zod schema
// is declared as z.ZodType<TPayload> so zodResolver infers the correct output type.
//
// Domain screens use this as:
//   <VersionedSettingsScreen<MyPayload>
//     schema={myZodSchema}
//     defaultValues={...}
//     renderFields={(form) => /* form is UseFormReturn<MyPayload> */}
//   />
interface VersionedSettingsScreenProps<TPayload extends FieldValues> {
  /** Numeric local PK of the tenant. Used in API paths. */
  tenantId: number
  /** Settings domain slug. */
  domain: SettingsDomain
  /** Page heading. */
  title: string
  /** One-line description rendered under the heading. */
  description: string
  /**
   * Zod schema — source of truth for form validation.
   *
   * Typed as z.ZodType<TPayload> so zodResolver gets the correct inferred type.
   */
  schema: z.ZodType<TPayload>
  /** Initial form values for the draft creation form. */
  defaultValues: DefaultValues<TPayload>
  /** Render the form fields inside the react-hook-form context. */
  renderFields: (form: UseFormReturn<TPayload>) => ReactNode
  /** Optional: render a summary line inside the version history table. */
  renderVersionSummary?: (v: SettingsVersion) => ReactNode
  /** Optional: target-tenant-id header value for platform-admin context. */
  targetTenantId?: string | null
}

// ---- Query key helpers -------------------------------------------------------

function activeSettingsKey(tenantId: number, domain: SettingsDomain) {
  return ['platform', 'settings', tenantId, domain, 'active'] as const
}

function versionsKey(tenantId: number, domain: SettingsDomain) {
  return ['platform', 'settings', tenantId, domain, 'versions'] as const
}

function approvalKey(publicId: string) {
  return ['platform', 'approval', publicId] as const
}

// ---- Status badge helper -----------------------------------------------------

function StatusBadge({ status }: { status: SettingsVersion['status'] }) {
  const map: Record<SettingsVersion['status'], { variant: 'success' | 'secondary' | 'default' | 'outline'; label: string }> = {
    active: { variant: 'success', label: 'Active' },
    draft: { variant: 'secondary', label: 'Draft' },
    pending_approval: { variant: 'default', label: 'Pending approval' },
    archived: { variant: 'outline', label: 'Archived' },
  }
  const { variant, label } = map[status] ?? { variant: 'outline', label: status }
  return <Badge variant={variant}>{label}</Badge>
}

// ---- Per-draft activate flow state -------------------------------------------

type ActivateState =
  | { phase: 'idle' }
  | { phase: 'submitting_approval' }
  | { phase: 'awaiting_checker'; approvalPublicId: string }
  | { phase: 'activating' }

// ---- Main component ----------------------------------------------------------

export function VersionedSettingsScreen<TPayload extends FieldValues>({
  tenantId,
  domain,
  title,
  description,
  schema,
  defaultValues,
  renderFields,
  renderVersionSummary,
  targetTenantId,
}: VersionedSettingsScreenProps<TPayload>) {
  const qc = useQueryClient()

  // ADR 0023 / 2026-09-28 backend fix: the 'reporting-retention' domain's
  // approval entity_type ('approval_policy') is now tenant-internal — owned
  // exclusively by /tenants/self/approvals/* (Slice 10 maker-checker-policy
  // activation reuses the same entity_type). POST /api/v1/approvals now
  // rejects it with 400 wrong_approval_surface, so the submit-approval step
  // below can never succeed for this domain any more. Derived from the shared
  // entity-type allow-list (not hardcoded to 'reporting-retention') so this
  // stays correct if another domain is ever remapped the same way.
  const approvalEntityType = DOMAIN_APPROVAL_ENTITY_TYPE[domain]
  const approvalSurfaceUnavailable = isTenantInternalApprovalEntityType(approvalEntityType)

  // Which draft is currently in the activate flow?
  const [activatingVersionId, setActivatingVersionId] = useState<number | null>(null)
  const [activateState, setActivateState] = useState<ActivateState>({ phase: 'idle' })
  const [showDraftForm, setShowDraftForm] = useState(false)

  // ---- Queries ---------------------------------------------------------------

  const activeQ = useQuery({
    queryKey: activeSettingsKey(tenantId, domain),
    queryFn: () => getActiveSettings(tenantId, domain, targetTenantId),
    // 404 means "no active version" — not a fatal error; the component
    // renders an EmptyState + CTA. All other errors are propagated normally.
    throwOnError: false,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false
      return failureCount < 2
    },
  })

  const versionsQ = useQuery({
    queryKey: versionsKey(tenantId, domain),
    queryFn: () => listSettingsVersions(tenantId, domain, targetTenantId),
    throwOnError: false,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false
      return failureCount < 2
    },
  })

  // Poll for approval state while in 'awaiting_checker' phase so the
  // "Activate now" button becomes available when the checker applies it.
  const approvalPublicIdToWatch =
    activateState.phase === 'awaiting_checker' ? activateState.approvalPublicId : undefined

  const approvalQ = useQuery({
    queryKey: approvalPublicIdToWatch
      ? approvalKey(approvalPublicIdToWatch)
      : ['platform', 'approval', 'none'],
    queryFn: () => getApproval(approvalPublicIdToWatch!),
    enabled: !!approvalPublicIdToWatch,
    refetchInterval: 15_000,
    staleTime: 5_000,
  })

  // ---- Draft creation mutation ------------------------------------------------

  // zodResolver's overloads don't accept z.ZodType<TPayload> when TPayload is a generic
  // parameter constrained to FieldValues — a known type-variance limitation in
  // @hookform/resolvers v5. Casting through `unknown` then back is the minimum viable
  // workaround. Runtime behaviour is correct: the schema validates TPayload correctly.
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */
  const schemaAsAny = schema as any
  const resolverForForm = zodResolver(schemaAsAny) as any
  const form = useForm<TPayload>({
    resolver: resolverForForm,
    defaultValues,
  })
  /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */

  const createDraftMut = useMutation({
    mutationFn: (payload: TPayload) =>
      createSettingsDraft(tenantId, domain, payload as Record<string, unknown>, targetTenantId),
    onSuccess: () => {
      toast.success('Draft created')
      void qc.invalidateQueries({ queryKey: versionsKey(tenantId, domain) })
      setShowDraftForm(false)
      typedForm.reset(defaultValues)
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Could not create draft'
      toast.error(msg)
    },
  })

  // ---- Approve-submit mutation ------------------------------------------------

  const submitApprovalMut = useMutation({
    mutationFn: (version: SettingsVersion) =>
      submitApproval({
        entity_type: DOMAIN_APPROVAL_ENTITY_TYPE[domain],
        entity_id: version.public_id,
        action: 'activate',
        reason: `Activate ${domain} settings version ${version.version}`,
      }),
    onSuccess: (appr: ApprovalRecord, version: SettingsVersion) => {
      toast.success(
        `Approval submitted (${appr.public_id.slice(0, 12)}). A checker must approve and apply it.`,
      )
      setActivatingVersionId(version.id)
      setActivateState({ phase: 'awaiting_checker', approvalPublicId: appr.public_id })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'wrong_approval_surface') {
        // Defense in depth: approvalSurfaceUnavailable / the disabled Activate
        // button below should prevent this mutation from ever firing for a
        // tenant-internal entity type, but keep the error message operator-safe
        // in case the entity-type mapping drifts in the future.
        toast.error(
          'This settings domain is decided by the tenant\'s own administrators, not the platform. Ask a tenant administrator to review it in their Approvals inbox.',
        )
      } else {
        toast.error(err instanceof Error ? err.message : 'Could not submit approval')
      }
      setActivateState({ phase: 'idle' })
    },
  })

  // ---- Activate mutation -----------------------------------------------------

  const activateMut = useMutation({
    mutationFn: ({
      version,
      approvalPublicId,
    }: {
      version: SettingsVersion
      approvalPublicId: string
    }) =>
      activateSettings(
        tenantId,
        domain,
        { settings_row_id: version.id, approval_public_id: approvalPublicId },
        targetTenantId,
      ),
    onSuccess: () => {
      toast.success(`${title} settings activated`)
      void qc.invalidateQueries({ queryKey: activeSettingsKey(tenantId, domain) })
      void qc.invalidateQueries({ queryKey: versionsKey(tenantId, domain) })
      setActivatingVersionId(null)
      setActivateState({ phase: 'idle' })
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 422) {
          toast.error('Approval required or not yet applied. Wait for the checker to apply the approval.')
        } else if (err.status === 409 && err.code === 'approval_entity_id_mismatch') {
          toast.error(
            'Approval entity ID mismatch. The approval was created for a different settings version. Submit a new approval.',
          )
          setActivateState({ phase: 'idle' })
        } else {
          toast.error(err.message)
        }
      } else {
        toast.error('Could not activate settings')
      }
    },
  })

  // ---- Handlers --------------------------------------------------------------

  const handleActivate = (version: SettingsVersion) => {
    if (approvalSurfaceUnavailable) {
      // The Activate button is disabled in this state (see ActivateButton),
      // this is belt-and-suspenders against a stale render reaching here.
      toast.error(
        'This settings domain is decided by the tenant\'s own administrators, not the platform. Ask a tenant administrator to review it in their Approvals inbox.',
      )
      return
    }
    if (activateState.phase === 'awaiting_checker' && activatingVersionId === version.id) {
      // The approval is already submitted for this version — check if it's applied.
      const appr = approvalQ.data
      if (appr?.state === 'applied') {
        setActivateState({ phase: 'activating' })
        activateMut.mutate({
          version,
          approvalPublicId: appr.public_id,
        })
      } else {
        toast.error(
          `Approval is in "${appr?.state ?? 'unknown'}" state. Wait for a checker to approve and apply it.`,
        )
      }
      return
    }
    // Start the maker-checker flow.
    setActivateState({ phase: 'submitting_approval' })
    submitApprovalMut.mutate(version)
  }

  // Cast through unknown to bridge the UseFormReturn<TPayload, any, TFieldValues> vs
  // UseFormReturn<TPayload> parameter mismatch. The form IS typed with TPayload;
  // TFieldValues is a RHF internal parameter that doesn't affect correctness here.
  const typedForm = form as unknown as UseFormReturn<TPayload>

  const handleCreateDraft = (payload: TPayload) => {
    createDraftMut.mutate(payload)
  }

  // ---- Active version card ---------------------------------------------------

  const is404 = activeQ.error instanceof ApiError && activeQ.error.status === 404
  const versions = versionsQ.data?.items ?? []
  const draftVersions = versions.filter((v) => v.status === 'draft')

  return (
    <div className="space-y-6">
      {/* Page header */}
      <header>
        <h2 className="text-xl font-bold tracking-tight">{title}</h2>
        <p className="mt-0.5 text-sm text-muted-fg">{description}</p>
      </header>

      {/* Tenant-internal approval surface note — by design, not a bug.
          Explains why Activate is disabled below rather than letting an
          operator think the feature broke. */}
      {approvalSurfaceUnavailable && (
        <div
          role="note"
          aria-label="About activating this settings domain"
          className="flex items-start gap-2 rounded border border-border bg-muted/20 px-3 py-2 text-xs text-muted-fg"
        >
          <Info size={12} className="mt-px shrink-0" aria-hidden />
          <span>
            <strong className="text-fg">
              Activating a draft here requires the tenant&apos;s own consent.
            </strong>{' '}
            This settings domain&apos;s approval is decided by the tenant&apos;s
            own administrators in their Approvals inbox, not by a platform
            admin — the platform can create and review drafts, but the tenant
            must approve activation. Ask a tenant administrator to activate the
            draft once it is ready.
          </span>
        </div>
      )}

      {/* Active version card */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/20">
          <div className="flex items-center gap-3">
            <CardTitle>Active version</CardTitle>
            <Badge variant="success" className="hidden sm:inline-flex">
              <ShieldCheck size={10} className="mr-1" aria-hidden /> Maker-checker enforced
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {activeQ.isLoading ? (
            <div className="space-y-2">
              <Skeleton />
              <Skeleton />
            </div>
          ) : is404 || !activeQ.data ? (
            <EmptyState
              icon={FileText}
              title="No active version"
              description={`No ${title.toLowerCase()} settings have been activated yet. Create a draft below and go through the maker-checker flow to activate.`}
              action={{
                label: 'Create first draft',
                onClick: () => setShowDraftForm(true),
                icon: FilePlus,
              }}
            />
          ) : activeQ.error ? (
            <div
              role="alert"
              className="flex items-center gap-2 rounded border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              <AlertTriangle size={14} aria-hidden />
              Could not load active settings. {activeQ.error.message}
            </div>
          ) : (
            <ActiveVersionCard
              version={activeQ.data}
              renderVersionSummary={renderVersionSummary}
            />
          )}
        </CardContent>
      </Card>

      {/* Version history */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/20">
          <div className="flex items-center justify-between">
            <CardTitle>Version history</CardTitle>
            {draftVersions.length > 0 && (
              <span className="rounded border border-status-pending/40 bg-status-pending/10 px-2 py-0.5 text-xs text-status-pending">
                <Clock size={10} className="mr-1 inline" aria-hidden />
                {draftVersions.length} draft{draftVersions.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {versionsQ.isLoading ? (
            <div className="space-y-1 p-4">
              <Skeleton />
              <Skeleton />
            </div>
          ) : versions.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-fg">
              No versions yet. Create the first draft below.
            </div>
          ) : (
            <VersionsTable
              versions={versions}
              activatingVersionId={activatingVersionId}
              activateState={activateState}
              approvalData={approvalQ.data}
              approvalSurfaceUnavailable={approvalSurfaceUnavailable}
              renderVersionSummary={renderVersionSummary}
              onActivate={handleActivate}
              isActivating={activateMut.isPending || submitApprovalMut.isPending}
            />
          )}
        </CardContent>
      </Card>

      {/* Create draft form */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/20">
          <div className="flex items-center justify-between">
            <CardTitle>Create draft</CardTitle>
            {!showDraftForm && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDraftForm(true)}
              >
                <FilePlus size={12} aria-hidden /> New draft
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {showDraftForm ? (
            <form
              onSubmit={(e) => { void typedForm.handleSubmit(handleCreateDraft)(e) }}
              className="space-y-4"
            >
              <div className="flex items-start gap-2 rounded border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-fg">
                <ShieldCheck size={12} className="mt-px text-primary shrink-0" aria-hidden />
                <span>
                  Creating a draft does not change live settings. A second operator must
                  approve and apply the change via the maker-checker flow before it becomes active.
                </span>
              </div>

              {renderFields(typedForm)}

              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={createDraftMut.isPending}
                >
                  {createDraftMut.isPending ? (
                    <RefreshCw size={12} className="animate-spin" aria-hidden />
                  ) : (
                    <FilePlus size={12} aria-hidden />
                  )}{' '}
                  Save draft
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={createDraftMut.isPending}
                  onClick={() => {
                    setShowDraftForm(false)
                    typedForm.reset(defaultValues)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-sm text-muted-fg">
              Click "New draft" to configure a new version. Drafts go through the maker-checker
              approval flow before becoming active.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Awaiting checker banner */}
      {activateState.phase === 'awaiting_checker' && (
        <div
          role="status"
          className="flex items-start gap-3 rounded border border-status-pending/40 bg-status-pending/10 px-4 py-3 text-sm text-status-pending"
        >
          <Clock size={16} className="mt-0.5 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">Awaiting checker approval</p>
            <p className="mt-0.5 text-xs">
              Approval <span className="font-mono">{activateState.approvalPublicId}</span> has been
              submitted. A different operator must approve and mark it applied in the Approvals
              Queue before you can activate.
            </p>
            {approvalQ.data?.state === 'applied' && (
              <p className="mt-1 text-xs font-medium text-status-success">
                Approval is applied — you can now activate the version above.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ---- Active version card inner component -------------------------------------

function ActiveVersionCard({
  version,
  renderVersionSummary,
}: {
  version: SettingsVersion
  renderVersionSummary?: (v: SettingsVersion) => ReactNode
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <StatusBadge status={version.status} />
        <span className="text-muted-fg">v{version.version}</span>
        <span className="font-mono text-xs text-muted-fg">{version.public_id}</span>
        <span className="ml-auto text-xs text-muted-fg">
          Activated{' '}
          {new Date(version.updated_at).toLocaleString(undefined, {
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })}
        </span>
      </div>
      {renderVersionSummary ? (
        <div className="rounded border border-border bg-muted/10 px-3 py-2 text-sm">
          {renderVersionSummary(version)}
        </div>
      ) : null}
    </div>
  )
}

// ---- Version history table ---------------------------------------------------

function VersionsTable({
  versions,
  activatingVersionId,
  activateState,
  approvalData,
  approvalSurfaceUnavailable,
  renderVersionSummary,
  onActivate,
  isActivating,
}: {
  versions: SettingsVersion[]
  activatingVersionId: number | null
  activateState: ActivateState
  approvalData: ApprovalRecord | undefined
  approvalSurfaceUnavailable: boolean
  renderVersionSummary?: (v: SettingsVersion) => ReactNode
  onActivate: (v: SettingsVersion) => void
  isActivating: boolean
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-fg">
          <tr>
            <th className="px-3 py-2 font-medium">Version</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Summary</th>
            <th className="px-3 py-2 font-medium">Created</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => {
            const isThisActivating = activatingVersionId === v.id
            const approvalState = isThisActivating ? approvalData?.state : undefined
            const approvalApplied = approvalState === 'applied'

            return (
              <tr
                key={v.id}
                className="border-b border-border last:border-b-0 hover:bg-muted/20"
              >
                <td className="px-3 py-2.5">
                  <span className="font-mono text-xs">v{v.version}</span>
                  <span className="ml-2 font-mono text-[10px] text-muted-fg">
                    {v.public_id.slice(0, 12)}…
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <StatusBadge status={v.status} />
                </td>
                <td className="max-w-xs px-3 py-2.5 text-xs text-muted-fg">
                  {renderVersionSummary ? renderVersionSummary(v) : '—'}
                </td>
                <td className="px-3 py-2.5 font-mono text-xs text-muted-fg">
                  {new Date(v.created_at).toLocaleDateString(undefined, {
                    month: 'short',
                    day: '2-digit',
                    year: 'numeric',
                  })}
                </td>
                <td className="px-3 py-2.5">
                  {v.status === 'draft' ? (
                    <ActivateButton
                      version={v}
                      isThisActivating={isThisActivating}
                      activatePhase={isThisActivating ? activateState.phase : 'idle'}
                      approvalApplied={approvalApplied}
                      isActivating={isActivating}
                      approvalSurfaceUnavailable={approvalSurfaceUnavailable}
                      onActivate={onActivate}
                    />
                  ) : v.status === 'active' ? (
                    <span className="inline-flex items-center gap-1 text-xs text-status-success">
                      <CheckCircle2 size={11} aria-hidden /> Live
                    </span>
                  ) : (
                    <span className="text-xs text-muted-fg">—</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---- Activate button with state machine rendering ----------------------------

function ActivateButton({
  version,
  isThisActivating,
  activatePhase,
  approvalApplied,
  isActivating,
  approvalSurfaceUnavailable,
  onActivate,
}: {
  version: SettingsVersion
  isThisActivating: boolean
  activatePhase: ActivateState['phase']
  approvalApplied: boolean
  isActivating: boolean
  approvalSurfaceUnavailable: boolean
  onActivate: (v: SettingsVersion) => void
}) {
  if (approvalSurfaceUnavailable) {
    return (
      <span
        className="text-xs text-muted-fg"
        title="This settings domain is decided by the tenant's own administrators, not the platform."
      >
        Requires tenant approval
      </span>
    )
  }

  if (!isThisActivating) {
    return (
      <Button
        size="sm"
        variant="primary"
        disabled={isActivating}
        onClick={() => onActivate(version)}
        data-testid={`activate-${version.id}`}
      >
        Activate
      </Button>
    )
  }

  switch (activatePhase) {
    case 'submitting_approval':
      return (
        <Button size="sm" variant="ghost" disabled>
          <RefreshCw size={11} className="animate-spin" aria-hidden /> Submitting…
        </Button>
      )
    case 'awaiting_checker':
      if (approvalApplied) {
        return (
          <Button
            size="sm"
            variant="primary"
            onClick={() => onActivate(version)}
            disabled={isActivating}
            data-testid={`activate-now-${version.id}`}
          >
            <CheckCircle2 size={11} aria-hidden /> Activate now
          </Button>
        )
      }
      return (
        <span className="inline-flex items-center gap-1 text-xs text-status-pending">
          <Clock size={11} aria-hidden /> Awaiting approval
        </span>
      )
    case 'activating':
      return (
        <Button size="sm" variant="ghost" disabled>
          <RefreshCw size={11} className="animate-spin" aria-hidden /> Activating…
        </Button>
      )
    default:
      return (
        <Button
          size="sm"
          variant="primary"
          onClick={() => onActivate(version)}
          disabled={isActivating}
          data-testid={`activate-${version.id}`}
        >
          Activate
        </Button>
      )
  }
}
